import { useRef, useState } from 'react';
import { CompactBand, BandTab, BandButton } from '@/components/CompactBand';
import type { DocTab } from '../types';
import { FAX_ADDRESS, money } from '../utils';

interface Props {
  tab: DocTab;
  todayCount: number;
  needsCount: number;
  filedCount: number;
  cardsToCharge: number;
  cardsToChargeTotal: number;
  checksCount: number;
  checksNotInBank: number;
  faxSetUp: boolean;
  onSetUpFax: () => void;
  onUpload: () => void;
  tabTo: (tab: DocTab) => string;
}

function buildHeadline(nToday: number, nNeeds: number): React.ReactNode {
  if (nToday === 0) return 'No documents came in today.';
  const lead = `${String(nToday)} documents came in today.`;
  if (nNeeds <= 0) return lead;
  const em = nNeeds === 1 ? 'One needs a person to read it.' : `${String(nNeeds)} need a person to read them.`;
  return <>{lead} <em>{em}</em></>;
}

export function Header({
  tab, todayCount, needsCount, filedCount, cardsToCharge, cardsToChargeTotal, checksCount, checksNotInBank,
  faxSetUp, onSetUpFax, onUpload, tabTo,
}: Props) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);

  const copyFax = () => {
    try { void navigator.clipboard.writeText(FAX_ADDRESS); } catch { /* clipboard unavailable */ }
    setCopied(true);
    if (timer.current != null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => { setCopied(false); }, 2000);
  };

  const right = (
    <>
      {faxSetUp ? (
        <>
          <span>Fax to ELVA</span>
          <span style={{ fontFamily: 'monospace', color: '#0f1f1e' }}>{FAX_ADDRESS}</span>
          <button
            type="button"
            className="cbd-lk"
            onClick={copyFax}
            style={{ background: 'none', border: 0, padding: 0, font: 'inherit', cursor: 'pointer' }}
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </>
      ) : (
        <BandButton variant="g" onClick={onSetUpFax}>Set up fax</BandButton>
      )}
      <BandButton variant="w" onClick={onUpload}>Upload documents</BandButton>
    </>
  );

  const tabs = (
    <>
      <BandTab
        label="Documents"
        meta={`${String(needsCount)} needs you`}
        active={tab === 'documents'}
        to={tabTo('documents')}
      />
      <BandTab
        label="Filed documents"
        meta={filedCount.toLocaleString()}
        active={tab === 'filed'}
        to={tabTo('filed')}
      />
      <BandTab
        label="Virtual cards"
        meta={`${String(cardsToCharge)} · ${money(cardsToChargeTotal)} to charge`}
        active={tab === 'cards'}
        to={tabTo('cards')}
      />
      <BandTab
        label="Checks"
        meta={`${String(checksCount)} · ${String(checksNotInBank)} not in the bank yet`}
        active={tab === 'checks'}
        to={tabTo('checks')}
      />
    </>
  );

  return <CompactBand headline={buildHeadline(todayCount, needsCount)} right={right} tabs={tabs} />;
}
